import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import useAuth from "../hooks/useAuth";
import Authorize from "./Authorize";
import { ModeAuthorizeEnum } from "../utils/constants";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import { Typography } from "@mui/material";
import PropTypes from "prop-types";

function LinearProgressWithLabel(props) {
  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}
    >
      <Box sx={{ width: "100%", mb: 1 }}>
        <LinearProgress variant="determinate" {...props} color="info" />
      </Box>
      <Box sx={{ minWidth: 35 }}>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {`${Math.round(props.value)}%`}
        </Typography>
      </Box>
    </Box>
  );
}

LinearProgressWithLabel.propTypes = {
  value: PropTypes.number.isRequired,
};

// eslint-disable-next-line react/prop-types
const ProtectedRoute = ({ children, access, permission }) => {
  const { accessToken, user, isLoading } = useAuth();
  const [progress, setProgress] = useState(0);
  const [isProgressComplete, setIsProgressComplete] = useState(false);

  useEffect(() => {
    // Reset trạng thái khi isLoading thay đổi
    setProgress(0);
    setIsProgressComplete(false);

    // Hàm chạy thanh tiến trình từ 0% đến 100%
    const runProgress = () => {
      let progressValue = 0;
      const interval = setInterval(() => {
        progressValue += 2; // Tăng 2% mỗi 40ms
        setProgress(progressValue);
        if (progressValue >= 100) {
          clearInterval(interval);
          setIsProgressComplete(true); // Đánh dấu tiến trình hoàn tất
        }
      }, 40); // Tổng thời gian: 40ms * 50 lần = 2000ms (2 giây)

      return () => clearInterval(interval);
    };

    // Chạy tiến trình nếu isLoading hoặc đang chờ khôi phục
    if (isLoading || (!accessToken && Object.keys(user || {}).length > 0)) {
      runProgress();
    } else {
      // Nếu không cần chờ, đánh dấu tiến trình hoàn tất ngay
      setProgress(100);
      setIsProgressComplete(true);
    }
  }, [isLoading, accessToken, user]);

  // Hiển thị thanh tiến trình khi chưa hoàn tất
  if (
    (isLoading || (!accessToken && Object.keys(user || {}).length > 0)) &&
    !isProgressComplete
  ) {
    return (
      <Box
        sx={{ width: "100%", position: "fixed", top: 0, left: 0, zIndex: 9999 }}
      >
        <LinearProgressWithLabel value={progress} />
      </Box>
    );
  }

  // Chỉ render logic chuyển hướng hoặc children sau khi tiến trình hoàn tất
  if (isProgressComplete) {
    // Nếu không có accessToken và không có user, chuyển hướng về /sign-in
    if (!accessToken && Object.keys(user || {}).length === 0) {
      if (access == 0 || access == 2 || access == 3) {
        return children; // Route công khai, không cần đăng nhập
      }
      return <Navigate to="/sign-in" replace />;
    }

    // Nếu có user nhưng không có accessToken, vẫn cho phép truy cập route công khai
    if (access == 0 || access == 1) {
      return (
        <Authorize mode={ModeAuthorizeEnum.access} permission={permission}>
          {children}
        </Authorize>
      );
    }

    return <Navigate to="/dashboard" replace />;
  }

  // Mặc định hiển thị thanh tiến trình nếu chưa hoàn tất
  return (
    <Box
      sx={{ width: "100%", position: "fixed", top: 0, left: 0, zIndex: 9999 }}
    >
      <LinearProgressWithLabel value={progress} />
    </Box>
  );
};

export default ProtectedRoute;
